import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { Virtuoso } from 'react-virtuoso'
// evertel
import { Spinner, InfoBox, Row, Modal, ModalBody, ModalHeader, useUI, Text } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { MessageReadsController } from '@evertel/message'
import { UserTileRawData } from '@evertel/web/user'
import { useUserTagline } from '@evertel/blue-user'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataRead } from '@evertel/types'

const BASE_HEIGHT_EMPTY = 200
const BASE_HEIGHT_DEFAULT = 150
const READ_ITEM_HEIGHT = 56

interface ReadByModalProps {
    visible: boolean
    messageId?: number
    modelType: 'room' | 'thread'
    onClose?: () => void
    onCloseDone?: () => void
}

const ReadByModal: React.FC<ReadByModalProps> = observer(({
    visible,
    messageId,
    modelType,
    onClose,
    onCloseDone
}) => {

    const sessionService = useService(SessionService, [])
    const messageReadsController = useService(MessageReadsController, [])
    const { addToast } = useUI()

    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        // only initialize if model is visible
        if (visible) {
            setIsLoading(true)

            try {
                messageReadsController.init(messageId, modelType)

            } catch (error) {
                console.error(error.message)

                addToast({
                    color: 'danger',
                    message: error.message
                })
            }

            setIsLoading(false)
        }

    }, [visible, messageId, messageReadsController, modelType, addToast])

    const onEndReached = () => {
        if (visible) {
            messageReadsController.fetchNextPage()
        }
    }

    const readsCount = messageReadsController.readsCount
    const title = `${readsCount} ${(readsCount > 1) ? 'people have' : 'person has'} read this message...`
    const modalHeight = (readsCount === 0 ? BASE_HEIGHT_EMPTY : BASE_HEIGHT_DEFAULT) + readsCount * READ_ITEM_HEIGHT

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            onCloseDone={onCloseDone}
            style={{
                height: modalHeight,
                maxHeight: '80vh'
            }}
        >
            <ModalHeader
                title={(isLoading) ? 'Loading reads...' : title}
                closeButton
            />
            <ModalBody>
                <Virtuoso
                    endReached={onEndReached}
                    data={messageReadsController.reads}
                    totalCount={readsCount}
                    overscan={300}
                    itemContent={(_, data) => {
                        return (
                            <UserReadRow read={data} departmentId={sessionService.selectedDepartmentId} modelType={modelType} />
                        )
                    }}
                    components={{
                        Footer: () => {
                            return (
                                <Footer
                                    showSpinner={isLoading}
                                />
                            )
                        },
                        EmptyPlaceholder
                    }}
                />
            </ModalBody>
        </Modal>
    )
})

interface UserProps {
    read: APIDataRead
    departmentId: number
    modelType: string
}

const UserReadRow: React.FC<UserProps> = observer(({
    read,
    departmentId,
    modelType
}) => {
    const user = read?.readBy

    const { displayPosition, isGuest } = useUserTagline({
        departmentsAccess: user?.departmentsAccess || [],
        contextDepartmentId: modelType === 'room' ? departmentId : undefined,
        reportingDepartment: user?.reportingDepartment
    })

    if (!user) {
        return (
            <Row className="border-bottom pb-2 mb-2">
                <Text italic>Could not load user</Text>
            </Row>
        )
    }

    const formattedDate = moment(read.createdDate).calendar(null, {
        sameDay: '[Today at] H:mm',
        lastDay: '[Yesterday at] H:mm',
        sameElse: 'MM/DD/YYYY @ H:mm'
    })

    return (
        <UserTileRawData
            user={user}
            className="border-bottom pb-2 mb-2"
            isGuest={isGuest}
            subComponent={
                <Text size="small">
                    {displayPosition}
                </Text>
            }
            rightColComponent={
                <Text size="smaller" className="date-cell">
                    Read on:<br />
                    {formattedDate}
                </Text>
            }
        />
    )
})

const Footer = ({ showSpinner }) => {
    if (!showSpinner) return null
    return (
        <i>
            <Spinner /> Loading...
        </i>
    )
}

const EmptyPlaceholder = () => {
    return (
        <InfoBox color="info">
            No one has read to this message yet
        </InfoBox>
    )
}

export { ReadByModal }
